import type { ReactNode } from 'react';
import { create } from 'zustand';

import STORAGE_KEYS from '@/configs/local-storage-keys';
import { setWorkspaceStorageItem } from '@/hooks/pure/useWorkspaceStorage';

let globalLoadingTimer: NodeJS.Timeout;

export class GlobalLoadingState {
  status: boolean;
  message?: ReactNode;

  constructor(status: boolean, message?: ReactNode) {
    this.status = status;
    this.message = message;
  }
}

type State = {
  sidebarHidden: boolean;
  feedbackDetailFullView: boolean;
  colorMode: ColorMode;
  globalLoading: GlobalLoadingState;
  systemTheme: ColorTheme;
};

type Action = {
  setSidebarHidden: (status: boolean) => void;
  setColorMode: (mode: ColorMode) => void;
  setFeedbackDetailFullView: (status: boolean) => void;
  toggleFeedbackDetailFullView: () => void;
  setGlobalLoading: (status: boolean | GlobalLoadingState) => void;
  setSystemTheme: (theme: ColorTheme) => void;
};

export const SIDEBAR_FULL_WIDTH = 250;
export const SIDEBAR_SHRINKED_WIDTH = 48;

export const getSystemTheme = (): ColorTheme => {
  // if (typeof window === 'undefined') return 'light';
  // return window.matchMedia('(prefers-color-scheme: dark)').matches
  //   ? 'dark'
  //   : 'light';
  return 'light';
};

const initialState: State = {
  sidebarHidden: true,
  colorMode: 'system',
  feedbackDetailFullView: false,
  globalLoading: new GlobalLoadingState(false, undefined),
  systemTheme: 'light',
};

const useUIStore = create<State & Action>((set, get) => {
  // Initialize system theme detection

  return {
    ...initialState,
    setSidebarHidden: (status) => {
      set((state) => ({
        ...state,
        sidebarHidden: status,
      }));
    },
    setColorMode: (mode) => {
      try {
        localStorage.setItem(STORAGE_KEYS.SYNCLY_COLOR_MODE, mode);
        set((state) => ({
          ...state,
          colorMode: mode,
        }));
      } catch (e) {
        return false;
      }
    },
    setSystemTheme: (theme) => {
      set((state) => ({
        ...state,
        systemTheme: theme,
      }));
    },
    setFeedbackDetailFullView(status) {
      setWorkspaceStorageItem(
        STORAGE_KEYS.FEEDBACK_DETAIL_FULL_VIEW,
        status ? 'true' : 'false',
      );
      set({
        feedbackDetailFullView: status,
      });
    },
    toggleFeedbackDetailFullView() {
      get().setFeedbackDetailFullView(!get().feedbackDetailFullView);
    },
    setGlobalLoading(status) {
      clearTimeout(globalLoadingTimer);
      if (typeof status === 'boolean') {
        if (status) {
          set({
            globalLoading: new GlobalLoadingState(status),
          });
        } else {
          set((state) => ({
            globalLoading: new GlobalLoadingState(
              false,
              state.globalLoading.message,
            ),
          }));
          globalLoadingTimer = setTimeout(() => {
            set({
              globalLoading: new GlobalLoadingState(false),
            });
          }, 500);
        }
        return;
      } else if (status instanceof GlobalLoadingState) {
        set((state) => ({
          ...state,
          globalLoading: new GlobalLoadingState(status.status, status.message),
        }));
        return;
      }
      throw new Error('Invalid parameter type for setGlobalLoading');
    },
  };
});

export default useUIStore;
